import { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { ResponseCodeError } from "./fetch";
import { getAuthStateHolder, redirectToSSOLanding } from "./sso";
export function useAsync(action, defaultValue, errorHandler) {
    const [value, setValue] = useState(defaultValue);
    useEffect(() => {
        action()
            .then(val => setValue(val))
            .catch(errorHandler || console.error);
    }, [action]);
    return value;
}
export function useObservable(observable, init) {
    const [data, setData] = useState(init);
    useEffect(() => {
        const subscription = observable.subscribe(setData);
        return () => {
            subscription.unsubscribe();
        };
    }, [observable, setData]);
    return data;
}
export function useSubmission(defaultMessage = "请求失败，请稍后重试") {
    const [submitting, setSubmitting] = useState(false);
    const doSubmit = async (submitFunc) => {
        if (submitting) {
            return;
        }
        setSubmitting(true);
        try {
            return await submitFunc();
        }
        catch (e) {
            message.error(ResponseCodeError.getMessage(e, defaultMessage));
        }
        finally {
            setSubmitting(false);
        }
    };
    return [doSubmit, submitting];
}
export function useBeforeUnload(alert) {
    useEffect(() => {
        const callback = (e) => {
            if (alert) {
                e.returnValue = "系统可能不会保存您所做的更改";
            }
        };
        window.addEventListener("beforeunload", callback);
        return () => {
            window.removeEventListener("beforeunload", callback);
        };
    }, [alert]);
}
export function useSingletonTimeout() {
    const handleRef = useRef();
    return useMemo(() => {
        const cancel = () => {
            if (handleRef.current !== undefined) {
                window.clearTimeout(handleRef.current);
                handleRef.current = undefined;
            }
        };
        return ({
            hasSchedule: () => handleRef.current !== undefined,
            schedule(callback, timeMs) {
                cancel();
                handleRef.current = window.setTimeout(() => {
                    handleRef.current = undefined;
                    callback();
                }, timeMs);
            },
            scheduleIfIdle(callback, timeMs) {
                if (handleRef.current !== undefined) {
                    return;
                }
                handleRef.current = window.setTimeout(() => {
                    handleRef.current = undefined;
                    callback();
                }, timeMs);
            },
            cancel
        });
    }, []);
}
export var AuthState;
(function (AuthState) {
    // 初始，检查请求还未返回
    AuthState[AuthState["INITIAL"] = 0] = "INITIAL";
    // 初始状态即已经登录
    AuthState[AuthState["INITIAL_AUTHED"] = 1] = "INITIAL_AUTHED";
    // 初始状态即未登录
    AuthState[AuthState["INITIAL_UNAUTHED"] = 2] = "INITIAL_UNAUTHED";
    // 状态从登录变为未登录
    AuthState[AuthState["TRANSIT_AUTHED"] = 3] = "TRANSIT_AUTHED";
    // 状态从未登录变为登录
    AuthState[AuthState["TRANSIT_UNAUTHED"] = 4] = "TRANSIT_UNAUTHED";
})(AuthState || (AuthState = {}));
function getAuthState(initial, auth) {
    if (auth === undefined) {
        return AuthState.INITIAL;
    }
    if (initial) {
        return auth ? AuthState.INITIAL_AUTHED : AuthState.INITIAL_UNAUTHED;
    }
    else {
        return auth ? AuthState.TRANSIT_AUTHED : AuthState.TRANSIT_UNAUTHED;
    }
}
/**
 * React Hook
 * 返回SSO登录态。
 */
export function useSSOAuth(redirectToLogin = false) {
    const authStateHolder = getAuthStateHolder();
    const [authenticated, setAuthenticated] = useState(getAuthState(true, authStateHolder.currentAuth()));
    useEffect(() => {
        const sub = authStateHolder.observeAuth(auth => setAuthenticated(prevState => getAuthState(prevState === AuthState.INITIAL, auth)));
        return () => sub.unsubscribe();
    }, []);
    if (redirectToLogin && authenticated === AuthState.INITIAL_UNAUTHED) {
        redirectToSSOLanding();
    }
    return authenticated;
}
